import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * Global role-filter context — the "workspace switcher" in the top nav.
 * Persists the user's chosen role to localStorage and broadcasts changes to
 * every consumer (Dashboard, EventsList, and the Shell top-nav render).
 *
 * `availableRoles` is populated by whichever consumer knows the portfolio
 * (typically the Dashboard when it loads events). The Shell hides the chip
 * bar until availableRoles has 2+ entries.
 */
const STORAGE_KEY = "dashboard_role_filter";

const RoleFilterContext = createContext({
  value: "all",
  setValue: () => {},
  availableRoles: [],
  setAvailableRoles: () => {},
});

export function RoleFilterProvider({ children }) {
  const [value, setValueState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || "all"; }
    catch { return "all"; }
  });
  const [availableRoles, setAvailableRoles] = useState([]);

  const setValue = useCallback((v) => {
    setValueState(v);
    try { localStorage.setItem(STORAGE_KEY, v); }
    catch (e) { console.debug("role filter persist failed", e); }
  }, []);

  // Keep multiple tabs in sync (rare but nice-to-have).
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY && e.newValue) setValueState(e.newValue);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const ctx = useMemo(() => ({ value, setValue, availableRoles, setAvailableRoles }),
    [value, setValue, availableRoles]);
  return <RoleFilterContext.Provider value={ctx}>{children}</RoleFilterContext.Provider>;
}

export function useRoleFilter() {
  return useContext(RoleFilterContext);
}

/**
 * Convenience helper that filters an events array by the current role
 * selection. Returns the same array (referentially stable) when
 * `value === "all"` so it can be safely used as a dependency.
 */
export function filterEventsByRole(events, value) {
  if (!value || value === "all") return events;
  return events.filter((e) => (e.role || "owner") === value);
}
